import React, { useState, useEffect, useCallback } from 'react'
import { navigate } from 'gatsby'
import DesktopNav from './DesktopNav'
import MobileNav from './MobileNav'
import BreadCrumb from '../../molecules/BreadCrumb/BreadCrumb'
import Hamburger from 'hamburger-react'
import SearchAutocomplete from '../../Search/SearchAutocomplete'
import Container from '@/components/Container'
import RehabPathLogoLinked from '../../molecules/RehabPathLogoLinked/RehabPathLogoLinked'
import { insuranceLayout } from '../../../constants/insuranceLayout'
import { isInsurancePageType } from '../../../domain/useCases/pageTypeCheck'
import { handleTopNavScrollColor } from '@/utils/handleTopNavScrollColor'
import { isCenterProfilePageType } from '../../../domain/useCases/pageTypeCheck'
import { isCenterEatingDisorders, isCenterSoberLiving } from '@/domain/center'
import { isTermEatingDisorders, isTermSoberLiving } from '@/domain/term'
import { isSSR } from '../../../utils/ssrUtils'
import IconHeartNav from '@/components/icons/IconHeartNav/IconHeartNav'

const TopHeader = ({
  drawer,
  toggleDrawer,
  scrollHandler,
  home,
  searchAutocompletePropsDesktop,
  isLuxury,
  isUnitedKingdomPage,
  isEatingDisorders,
  isSoberLiving,
  isProviders,
  department
}) => (
  <div
    className={`flex h-[60px] w-full flex-row transition-all duration-300 ease-out md:h-[72px] md:border-b ${scrollHandler}`}
  >
    <div className="mx-auto flex size-full max-w-screen-xl flex-col items-stretch justify-center px-4 md:px-6 lg:px-8">
      <div
        id="top-navigation"
        className={`flex items-center justify-between ${
          drawer ? 'overflow-hidden' : ''
        }`}
      >
        <div className="flex h-[22px] items-center pl-0.5 pr-2">
          <RehabPathLogoLinked
            isLuxury={isLuxury}
            isUnitedKingdomPage={isUnitedKingdomPage}
            isEatingDisorders={isEatingDisorders}
            isSoberLiving={isSoberLiving}
            isProviders={isProviders}
          />
        </div>
        {!home && (
          <div className="hidden max-w-xl flex-1 items-center justify-center md:flex">
            <SearchAutocomplete {...searchAutocompletePropsDesktop} />
          </div>
        )}
        <div className="hidden justify-self-end md:flex">
          <DesktopNav department={department} />
        </div>

        <div className="mr-2 flex items-center text-white md:hidden">
          <IconHeartNav
            className="mr-1"
            onClick={() => {
              navigate('/favorite')
            }}
          />
          <Hamburger
            toggled={drawer}
            toggle={toggleDrawer}
            color="#162941"
            distance="lg"
            rounded
            label="show menu"
            size={20}
            hideOutline={true}
          />
        </div>
        <div
          className={`visible fixed right-0 top-[60px] z-50 size-full bg-white transition-transform duration-200 md:hidden ${
            drawer ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <MobileNav
            className="flex size-full flex-col"
            closeDrawer={toggleDrawer}
          />
        </div>
      </div>
    </div>
  </div>
)

const SearchHeaderMobile = ({
  scrollHandler,
  searchAutocompletePropsMobile
}) => (
  <div
    className={`pb-0.5 transition-all duration-300 ease-in-out md:hidden ${scrollHandler}`}
  >
    <SearchAutocomplete {...searchAutocompletePropsMobile} />
  </div>
)

const TopNav = (props) => {
  const [drawer, setDrawer] = useState(false)
  const [scrollState, setScrollState] = useState({
    hasScrolled: false,
    hasScrolledMobile: false
  })
  const backgroundColor = props?.backgroundColor
  useEffect(() => {
    document.querySelector('html').style.overflowY = drawer ? 'hidden' : null
  }, [drawer])

  const toggleDrawer = useCallback(() => {
    setDrawer((prevDrawer) => !prevDrawer)
  }, [])

  const handleScroll = useCallback(() => {
    const scrolled = window.scrollY > 1
    const scrolledMobile = window.scrollY > 62
    setScrollState((prevState) => ({
      hasScrolled: scrolled,
      hasScrolledMobile: scrolledMobile
    }))
  }, [])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll])

  const scrollHandler = handleTopNavScrollColor({
    pageType: props?.pageType,
    term: props?.term?.slug,
    location: props?.location,
    hasScrolled: scrollState.hasScrolled,
    backgroundColor
  })

  const searchAutocompleteProps = {
    location: props.location,
    pageType: props.pageType,
    referralParamValue: 'searchboxHeader',
    isGlobalTerm: props?.term?.global
  }

  const searchAutocompletePropsDesktop = {
    ...searchAutocompleteProps,
    id: 'desktop-top-nav-autocomplete'
  }

  const searchAutocompletePropsMobile = {
    ...searchAutocompleteProps,
    id: 'mobile-top-nav-autocomplete'
  }

  const isEatingDisorders =
    isTermEatingDisorders(props?.term) || isCenterEatingDisorders(props?.center)

  const isSoberLiving =
    isTermSoberLiving(props?.term) || isCenterSoberLiving(props?.center)

  const isProviders = isSSR() ? false : location?.pathname?.includes('/pro/')

  const breadcrumbs = props.hideBreadCrumbs ? null : (
    <>
      {props.showBreadCrumbs && (
        <div
          className={`border-t ${
            isInsurancePageType(props.pageType) &&
            insuranceLayout[props?.term?.slug]?.style
              ? `border-t-insurance-bg-${props.term.slug} md:border-0`
              : 'border-t-gray-300 md:border-0'
          } md:border-0`}
        >
          <Container size="xl">
            <div className="flex h-12 items-center">
              <BreadCrumb
                center={props.center}
                term={props?.term}
                location={props?.location}
                title={props.title}
                pageType={props.pageType}
                shouldDefaultRegionalize={props?.shouldDefaultRegionalize}
                locationAllowList={props?.locationAllowList}
              />
            </div>
          </Container>
        </div>
      )}
    </>
  )
  if (isCenterProfilePageType(props.pageType)) {
    return (
      <>
        <TopHeader
          drawer={drawer}
          toggleDrawer={toggleDrawer}
          scrollHandler={scrollHandler}
          home={props.home}
          searchAutocompletePropsDesktop={searchAutocompletePropsDesktop}
          isLuxury={props.isLuxury}
          isUnitedKingdomPage={props.isUnitedKingdomPage}
          isEatingDisorders={isEatingDisorders}
          isSoberLiving={isSoberLiving}
          isProviders={isProviders}
        />
        <SearchHeaderMobile
          scrollHandler={scrollHandler}
          drawer={drawer}
          searchAutocompletePropsMobile={searchAutocompletePropsMobile}
        />
        {breadcrumbs}
      </>
    )
  }

  return (
    <>
      <header
        className={`top-0 z-30 w-full ${
          props.home ? 'sticky top-0' : 'md:sticky md:top-0'
        }`}
      >
        <TopHeader
          drawer={drawer}
          toggleDrawer={toggleDrawer}
          scrollHandler={scrollHandler}
          home={props.home}
          searchAutocompletePropsDesktop={searchAutocompletePropsDesktop}
          isLuxury={props.isLuxury}
          isUnitedKingdomPage={props.isUnitedKingdomPage}
          isEatingDisorders={isEatingDisorders}
          isSoberLiving={isSoberLiving}
          isProviders={isProviders}
          department={props?.department}
        />
      </header>
      {!props.home && (
        <div className="sticky top-0 z-20 md:z-30">
          <SearchHeaderMobile
            scrollHandler={scrollHandler}
            drawer={drawer}
            searchAutocompletePropsMobile={searchAutocompletePropsMobile}
          />
        </div>
      )}
      {breadcrumbs}
    </>
  )
}

export default TopNav
